// Чтение и запись настроек внешнего вида. Как язык и ширины панелей, это
// настройка вида, а не часть AppState: в экспорт JSON не попадает и на сервер
// не уезжает, поэтому живёт отдельным ключом в браузере.
import type { StorageAdapter } from '../store';
import {
  DEFAULT_DARK_THEME,
  DEFAULT_FONT_SCALE,
  DEFAULT_FONT_SCALE_INDEX,
  DEFAULT_SPACE_SCALE,
  DEFAULT_SPACE_SCALE_INDEX,
  DEFAULT_THEME,
  FONT_SCALES,
  SPACE_SCALES,
  isThemeId,
  nearestFontScaleIndex,
  nearestSpaceScaleIndex,
  type ThemeId,
} from './themes';

export const APPEARANCE_STORAGE_KEY = 'plan-flights.appearance.v1';

export interface Appearance {
  theme: ThemeId;
  /** Индексы ступеней, а не сами множители: ступени — это UI. */
  fontScaleIndex: number;
  spaceScaleIndex: number;
}

/**
 * Что показать тому, кто ничего не выбирал: системное «люблю тёмное» уважаем,
 * иначе светлая тема по умолчанию.
 */
export function defaultAppearance(prefersDark: boolean): Appearance {
  return {
    theme: prefersDark ? DEFAULT_DARK_THEME : DEFAULT_THEME,
    fontScaleIndex: DEFAULT_FONT_SCALE_INDEX,
    spaceScaleIndex: DEFAULT_SPACE_SCALE_INDEX,
  };
}

/** Любой мусор в хранилище трактуем как «настроек нет». */
export function readAppearance(storage: StorageAdapter, prefersDark: boolean): Appearance {
  const fallback = defaultAppearance(prefersDark);
  const raw = storage.get(APPEARANCE_STORAGE_KEY);
  if (!raw) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return fallback;
    const rec = parsed as Record<string, unknown>;
    return {
      theme: isThemeId(rec.theme) ? rec.theme : fallback.theme,
      // Хранится множитель, а не индекс: ступени могут поменяться между
      // версиями, а «мне нравилось на 10% крупнее» переживает такую правку.
      fontScaleIndex:
        rec.fontScale === undefined
          ? fallback.fontScaleIndex
          : nearestFontScaleIndex(rec.fontScale),
      spaceScaleIndex:
        rec.spaceScale === undefined
          ? fallback.spaceScaleIndex
          : nearestSpaceScaleIndex(rec.spaceScale),
    };
  } catch {
    return fallback;
  }
}

/**
 * Настройки уже лежат в хранилище — значит, человек их когда-то выбирал сам.
 * Отсюда видно разницу между «мне так нравится» и «меня так застали»: первое
 * подсказкой из ссылки перебивать нельзя, второе — можно.
 */
export function hasStoredAppearance(storage: StorageAdapter): boolean {
  return storage.get(APPEARANCE_STORAGE_KEY) !== null;
}

/** Часть настроек вида: в адресе может быть указано не всё сразу. */
export type AppearancePatch = Partial<Appearance>;

/**
 * Вид, закодированный в адресе: `?theme=darkula&fs=1.1&sp=1`.
 *
 * Так уезжает публичная ссылка на поездку — читатель открывает её ровно в той
 * теме и тех размерах, в которых поездку собирали (src/share/api.ts). Рядом
 * едет `?lang=`, но его разбирает I18nProvider: язык — не оформление.
 *
 * Как и в хранилище, ступени кегля и отступов передаются множителями, а не
 * индексами: набор ступеней от версии к версии меняется, а разосланные ссылки
 * остаются жить и должны попадать в «примерно так же», а не в чужую ступень.
 *
 * null — в адресе про оформление ничего нет; пустой объект отличать от него не
 * надо, поэтому и он превращается в null.
 */
export function appearanceFromParams(params: URLSearchParams): AppearancePatch | null {
  /**
   * Множитель из параметра или null. Пустой и нечисловой параметр — это не
   * ноль, а «не сказано»: Number('') даёт 0, и без этой проверки обрезанный
   * хвост ссылки молча вжимал бы страницу в самую тесную ступень.
   */
  const scale = (key: string): number | null => {
    const raw = params.get(key)?.trim();
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  };

  const patch: AppearancePatch = {};

  const theme = params.get('theme');
  if (isThemeId(theme)) patch.theme = theme;

  const font = scale('fs');
  if (font !== null) patch.fontScaleIndex = nearestFontScaleIndex(font);

  const space = scale('sp');
  if (space !== null) patch.spaceScaleIndex = nearestSpaceScaleIndex(space);

  return Object.keys(patch).length > 0 ? patch : null;
}

/** Обратная операция: вид — в параметры адреса, для сборки публичной ссылки. */
export function appearanceToParams(value: Appearance): Array<[string, string]> {
  return [
    ['theme', value.theme],
    ['fs', String(FONT_SCALES[value.fontScaleIndex] ?? DEFAULT_FONT_SCALE)],
    ['sp', String(SPACE_SCALES[value.spaceScaleIndex] ?? DEFAULT_SPACE_SCALE)],
  ];
}

export function writeAppearance(storage: StorageAdapter, value: Appearance): void {
  storage.set(
    APPEARANCE_STORAGE_KEY,
    JSON.stringify({
      theme: value.theme,
      fontScale: FONT_SCALES[value.fontScaleIndex],
      spaceScale: SPACE_SCALES[value.spaceScaleIndex],
    }),
  );
}

/** Системное предпочтение тёмного оформления; вне браузера — «нет». */
export function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}
